import { useState, useEffect } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, } from "@/utils/constants";
import { ICustomerDiscountDetail, ICustomerDiscountSearchFields, customerDiscountService, ICustomerDiscountFormFields, ICustomerDiscountInsert, ICustomerDiscountUpdate } from "./customerDiscountService";
import { useGlobalModel } from "@/model/globalModel";
import { convertRes2Blob } from "@/utils/exportFile";
import { ISearchPage, ISelectOption } from "@/types/FmsTypes";
import { transPgToPagination } from "@/views/base/app/appModel";
import { globalPrompt } from "@/components/YhMessage";

export interface IAuditDrawerType {
    visible: boolean,
    type: "audit" | "casher" | ""
}
export const useCustomerDiscountModel = createModel(function () {
    const { user: { shopId, shopName, shopCode, userName } } = useGlobalModel();
    /**
     * 列表
     */
    //列表数据源
    const [dataSource, setDataSource] = useState<ICustomerDiscountDetail[]>([]);
    //查询条件
    const [searchParam, setSearchParam] = useState<ICustomerDiscountSearchFields>({});
    //分页参数
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<ICustomerDiscountDetail[]>([]);
    const [exportLoading, setExportLoading] = useState(false)

    //列表查询
    const loadData = async (page: ISearchPage = initPgCfg, searchValue: ICustomerDiscountSearchFields = searchParam) => {
        const searchParams = {
            shopCode,
            ...searchValue,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await customerDiscountService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
        clearSelectData();
    }
    //设置搜索value
    const updateSearchParam = (updateParam: ICustomerDiscountSearchFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    //切换门店重置缓存
    useEffect(() => {
        setSearchParam({});
        setSelectedRowKeys([]);
        setAddFormVal({ ...initAddFormVal, shopName, createUser: userName });
    }, [shopId]);

    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }
    const clearSelectData = () => {
        setSelectedRowKeys([]);
    }

    /** 
     * 表单详情
     */

    //编辑表单缓存
    const [editFormVal, setEditFormVal] = useState<ICustomerDiscountFormFields>({});
    const [editcreditAcc, setEditCreditAcc] = useState<ISelectOption[]>([])
    const initAddFormVal = {
        shopName,
        createUser: userName
    }
    //新增表单缓存
    const [addFormVal, setAddFormVal] = useState<ICustomerDiscountFormFields>(initAddFormVal);
    //申请单 实体下面的授信账户
    const [creditAcc, setCreditAcc] = useState<ISelectOption[]>([])
    //表单新增修改
    const updateAddFormVal = (val: ICustomerDiscountFormFields | null) => {
        val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal(initAddFormVal);
    }
    //表单新增修改
    const updateEditFormVal = (val: ICustomerDiscountFormFields | null) => {
        val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal({});
    }
    //获取一条详情数据 Api
    const getCustomerDiscountOne = async (id: string) => {
        let { retData } = await customerDiscountService.one(id);
        retData && setEditFormVal(retData);
        return retData;
    }

    //表单新增 Api
    const insertCustomerDiscount = async (val: ICustomerDiscountFormFields) => {
        let data: ICustomerDiscountInsert = {
            ...val,
            shopName, shopCode,
        };
        let { retData } = await customerDiscountService.insert(data);
        if (retData) {
            globalPrompt("message", { text: "申请成功", type: "success" })
        } else {
            globalPrompt("message", { text: "申请失败", type: "error" });
        }
        return retData;
    }

    //表单编辑 Api
    const editCustomerDiscount = async (val: ICustomerDiscountFormFields & { id: string }) => {
        let data: ICustomerDiscountUpdate = {
            ...val
        };
        delete data.createTime;
        delete data.updateTime;
        let { retData } = await customerDiscountService.update(data);
        retData ?
            globalPrompt("message", { text: "编辑成功", type: "success" })
            : globalPrompt("message", { text: "编辑失败", type: "error" });
        return retData;
    }

    //导出
    const exportCustomerDiscount = async (searchValue: ICustomerDiscountSearchFields = searchParam) => {
        setExportLoading(true)
        try {
            const searchParams = {
                shopId,
                ...searchValue,
                pageSize: -1,
                pageNum: 1,
            };
            let result = await customerDiscountService.export(searchParams);
            convertRes2Blob(result);
            setExportLoading(false)
        } catch {
            setExportLoading(false)
        }
    }

    //机构 | 财务 审核
    const [auditDrawerVisible, setAuditDrawerVisible] = useState<IAuditDrawerType>({ visible: false, type: "" });
    const casherExamined = async (val: any) => {
        let data = {
            id: editFormVal.id ?? "",
            isAgre: val.isAgre,
            advise: val.advise,
        }
        let { retData } = await customerDiscountService.casherExamined(data);
        if (retData) {
            globalPrompt("message", { text: "操作成功", type: "success" });
            setAuditDrawerVisible({ visible: false, type: "" });
            getCustomerDiscountOne(editFormVal.id ?? "");
        }
    }

    const auditExamined = async (val: any) => {
        let data = {
            id: editFormVal.id ?? "",
            isAgre: val.isAgre,
            advise: val.advise,
        }
        let { retData } = await customerDiscountService.auditExamined(data);
        if (retData) {
            globalPrompt("message", { text: "操作成功", type: "success" });
            setAuditDrawerVisible({ visible: false, type: "" });
            getCustomerDiscountOne(editFormVal.id ?? "");
        }
    }

    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        editFormVal,
        addFormVal,
        auditDrawerVisible, setAuditDrawerVisible,
        creditAcc, setCreditAcc,
        editcreditAcc,
        setEditCreditAcc,
        exportLoading,
        /***method***/
        updateAddFormVal,
        updateEditFormVal,
        insertCustomerDiscount,
        editCustomerDiscount,
        getCustomerDiscountOne,
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        exportCustomerDiscount,
        auditExamined,
        casherExamined
    }
});